// Coordinator — NPS Analytics (Live API data)
import { useState, useEffect } from 'react';
import { TrendingUp, SmilePlus, Frown, Users, RefreshCw } from 'lucide-react';

interface NpsStats {
    avgScore: number;
    npsScore: number;
    totalResponses: number;
    promoters: number;
    passives: number;
    detractors: number;
}

interface NpsSurvey {
    id: string;
    score: number;
    comment: string | null;
    respondedAt: string;
    flagged: boolean;
    followupCompleted: boolean;
    patient: string;
    doctor: string | null;
    doctorColor: string;
}

import { API_BASE } from '../config/api';

function NpsGauge({ score, loading }: { score: number; loading: boolean }) {
    const pct = (score / 10) * 100;
    const color = score >= 8 ? 'var(--success)' : score >= 6 ? 'var(--warning)' : 'var(--danger)';
    return (
        <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto' }}>
                <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="10" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="10"
                        strokeDasharray={`${pct * 3.267} ${326.7 - pct * 3.267}`} strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.5s ease' }} />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color }}>
                    {loading ? '...' : score.toFixed(1)}
                </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 8 }}>NPS Promedio</div>
        </div>
    );
}

export function NpsPage() {
    const [stats, setStats] = useState<NpsStats>({ avgScore: 0, npsScore: 0, totalResponses: 0, promoters: 0, passives: 0, detractors: 0 });
    const [surveys, setSurveys] = useState<NpsSurvey[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadData(); }, []);

    async function loadData() {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/nps`);
            const json = await res.json();
            if (json.data) {
                setStats(json.data.stats);
                setSurveys(json.data.surveys);
            }
        } catch {
            console.warn('NPS API unavailable');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h1 className="page-title">
                    <SmilePlus size={22} style={{ display: 'inline', marginRight: 8 }} />
                    Análisis NPS
                </h1>
                <button className="btn btn-secondary" onClick={loadData} disabled={loading}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <RefreshCw size={14} className={loading ? 'spin' : ''} /> Actualizar
                </button>
            </div>

            <div className="stats-row">
                <div className="stat-card" style={{ display: 'flex', justifyContent: 'center' }}>
                    <NpsGauge score={stats.avgScore} loading={loading} />
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#D1FAE5', color: '#16A34A' }}><TrendingUp size={20} /></div>
                    <div className="stat-value" style={{ color: stats.npsScore >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {loading ? '...' : `${stats.npsScore > 0 ? '+' : ''}${stats.npsScore}`}
                    </div>
                    <div className="stat-label">Net Promoter Score</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#DBEAFE', color: '#1D4ED8' }}><Users size={20} /></div>
                    <div className="stat-value">{loading ? '...' : stats.totalResponses}</div>
                    <div className="stat-label">Respuestas totales</div>
                </div>
            </div>

            {/* Distribution */}
            <div className="card">
                <h3>Distribución</h3>
                <div style={{ display: 'flex', gap: 24 }}>
                    {[
                        { label: 'Promotores (9-10)', count: stats.promoters, pct: stats.totalResponses ? (stats.promoters / stats.totalResponses * 100) : 0, color: '#16A34A' },
                        { label: 'Pasivos (7-8)', count: stats.passives, pct: stats.totalResponses ? (stats.passives / stats.totalResponses * 100) : 0, color: '#F59E0B' },
                        { label: 'Detractores (1-6)', count: stats.detractors, pct: stats.totalResponses ? (stats.detractors / stats.totalResponses * 100) : 0, color: '#DC2626' },
                    ].map(d => (
                        <div key={d.label} style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 500 }}>{d.label}</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: d.color }}>
                                    {loading ? '...' : `${d.count} (${d.pct.toFixed(0)}%)`}
                                </span>
                            </div>
                            <div className="nps-bar">
                                <div className="nps-bar-fill" style={{ width: `${d.pct}%`, background: d.color, transition: 'width 0.4s ease' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recent Scores */}
            <div className="card">
                <h3>Respuestas recientes</h3>
                <table className="table">
                    <thead><tr><th>Paciente</th><th>Médico</th><th>Puntuación</th><th>Comentario</th><th>Fecha</th><th>Estado</th></tr></thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <tr key={i}>{Array.from({ length: 6 }).map((_, j) => (
                                    <td key={j}><div style={{ height: 16, background: 'var(--bg-2)', borderRadius: 4 }} /></td>
                                ))}</tr>
                            ))
                        ) : surveys.length === 0 ? (
                            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>No hay datos NPS</td></tr>
                        ) : (
                            surveys.map(s => (
                                <tr key={s.id} style={{ background: s.flagged && !s.followupCompleted ? '#FEF2F2' : undefined }}>
                                    <td style={{ fontWeight: 500 }}>{s.patient}</td>
                                    <td>
                                        {s.doctor ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.doctorColor, display: 'inline-block' }} />
                                                {s.doctor}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        <span style={{
                                            fontWeight: 700, fontSize: 16,
                                            color: s.score >= 9 ? '#16A34A' : s.score >= 7 ? '#F59E0B' : '#DC2626',
                                        }}>
                                            {s.score >= 9 ? <SmilePlus size={14} style={{ marginRight: 4 }} /> :
                                                s.score < 7 ? <Frown size={14} style={{ marginRight: 4 }} /> : null}
                                            {s.score}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {s.comment || '—'}
                                    </td>
                                    <td style={{ fontSize: 13 }}>
                                        {s.respondedAt ? new Date(s.respondedAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) : '—'}
                                    </td>
                                    <td>
                                        {s.flagged
                                            ? s.followupCompleted
                                                ? <span className="badge badge-success">✓ Seguimiento hecho</span>
                                                : <span className="badge badge-danger">⚠ Seguimiento</span>
                                            : <span className="badge badge-success">OK</span>}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
